/**
 * Polyfills mínimos orientados a Safari / WebKit.
 * Solo se aplica lo que el código de la app usa y que puede faltar
 * en Safari antiguo o WebViews embebidos.
 */

/** Formato de `crypto.randomUUID()` tal como lo tipa lib.dom. */
type UUID = `${string}-${string}-${string}-${string}-${string}`;

/**
 * Instala `crypto.randomUUID` cuando el entorno no lo trae.
 *
 * Falta en Safari < 15.4 y en algunos WKWebView, pero sobre todo **falta en
 * cualquier navegador fuera de un contexto seguro** (HTTP plano): la API está
 * restringida a contextos seguros, mientras que `crypto.getRandomValues` —la
 * fuente de entropía que usamos aquí— sí está disponible siempre.
 *
 * @param target Objeto `Crypto` a parchear. Se inyecta en los tests; en runtime
 *   se usa el del entorno.
 */
export function polyfillRandomUUID(target: Crypto | undefined = globalThis.crypto): void {
  if (!target || typeof target.randomUUID === 'function') {
    return;
  }

  // Algunos WebView exponen `crypto` como no extensible; si falla, degrada
  // en silencio en lugar de romper el bootstrap de la app.
  try {
    Object.defineProperty(target, 'randomUUID', {
      configurable: true,
      enumerable: false,
      writable: true,
      value: function randomUUID(): UUID {
        const bytes = new Uint8Array(16);
        target.getRandomValues(bytes);
        // RFC 4122: versión 4 en el nibble alto del byte 6, variante 10x en el 8.
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` as UUID;
      },
    });
  } catch {
    // Sin polyfill: los llamadores de crypto.randomUUID() fallarán de forma explícita.
  }
}

polyfillRandomUUID();
